import {
  TActorCardColumnKey,
  TActorCardSortBy,
  TActorCardViewMode,
  TActorFavoritesViewMode,
  TActorSortBy,
  TActorViewMode,
  TCastingSortBy,
  TCastingViewMode,
  TExportFormat,
  TFavoriteActorColumnKey,
  TProjectSortBy,
  TProjectViewMode,
  TSortOrder
} from '@/shared/types/ListDisplay';

/**
 * Режимы отображения карточек актёра — единая лестница плотности (18976), одна на
 * все ширины экрана. Раньше домен резался на `ACTOR_VIEW_MODES_WIDE`/`_TOUCH` по
 * порогу тач-плотности (750px): «Компактно» и «Лента фото» исчезали на узких
 * экранах, а «Крупно» появлялось только там. Деградация убрана: на узких ширинах
 * переключатель уже рисует только иконки (кнопка дорастает до 44×44 —
 * `ViewModeSwitch.tsx`), места в ряду хватает без сокращения домена.
 *
 * Вид «Крупно» (`large`) убран из словаря (решение заказчика): на узких экранах
 * «Карточки» теперь сами дают одну карточку в ряд — то, ради чего «Крупно» и
 * существовало (см. `ACTOR_CARD_GRID_MIN_WIDTH` в `cardGrid.ts`), а на широких —
 * сетку. Сохранённый `large` читается как `card` миграцией `5`
 * (`app/store/migrations.ts`).
 */
export const ACTOR_VIEW_MODES: TActorViewMode[] = [
  'card',
  'list',
  'compact',
  'photos'
];

/**
 * Режимы отображения избранного режиссёра — та же лестница плюс `table` последним
 * (18976). Домен от ширины экрана тоже не зависит — той же причиной, что у
 * `ACTOR_CARD_VIEW_MODES`: `table` скроллится по горизонтали и не деградирует.
 */
export const ACTOR_FAVORITES_VIEW_MODES: TActorFavoritesViewMode[] = [
  'card',
  'list',
  'compact',
  'photos',
  'table'
];

/** Режимы отображения карточек кастинга */
export const CASTING_VIEW_MODES: TCastingViewMode[] = [
  'card',
  'list',
  'compact'
];

/** Режимы отображения карточек проекта */
export const PROJECT_VIEW_MODES: TProjectViewMode[] = [
  'card',
  'list',
  'compact'
];

/**
 * Опции сортировки списка актёров в ПОИСКЕ (`users#search`), где `created_at` — дата
 * регистрации профиля.
 *
 * Подпись называет критерий, а не «новизну», и это не косметика: ключ `created_at`
 * одинаков на четырёх актёрских экранах, а означает ТРИ разные даты — регистрацию
 * профиля (поиск), добавление в закладки (избранное) и дату отклика (хаб откликов,
 * таб «Откликнувшиеся»). Одна подпись на три смысла врала бы на двух экранах из трёх,
 * поэтому набор подписей — по одному на смысл: этот, `ACTOR_BOOKMARK_SORT_OPTIONS` и
 * `ACTOR_RESPONSE_SORT_OPTIONS`. Домен самих ключей у них общий (`ACTOR_SORT_KEYS`).
 */
export const ACTOR_SORT_OPTIONS = [
  { value: 'created_at', label: 'По дате регистрации' },
  { value: 'name', label: 'По алфавиту' },
  { value: 'age', label: 'По возрасту' }
] as const;

/**
 * Опции сортировки списка ИЗБРАННЫХ актёров.
 *
 * Отличается от `ACTOR_SORT_OPTIONS` первым пунктом, и не только подписью:
 * `bookmarks#users` различает «`sort_by` не пришёл» (порядок добавления в закладки) и
 * «`sort_by=created_at`» (дата регистрации профиля) — гейт стоит на
 * `sort_by_requested?`. Экран избранного шлёт «По дате добавления» первым способом —
 * то есть НЕ шлёт ключ сортировки вовсе, — а без ключа сервер игнорирует и
 * `sort_order`. Отсюда `nonDirectional`: стрелка направления у такого пункта была бы
 * обманом, перевернуть нечего.
 */
export const ACTOR_BOOKMARK_SORT_OPTIONS = [
  { value: 'created_at', label: 'По дате добавления', nonDirectional: true },
  { value: 'name', label: 'По алфавиту' },
  { value: 'age', label: 'По возрасту' }
] as const;

/**
 * Опции сортировки списков ОТКЛИКОВ — хаб откликов и таб «Откликнувшиеся» кастинга.
 *
 * `created_at` в `responses#index` и `castings#responses` — дата самого отклика или
 * приглашения, а не дата регистрации профиля (см. `useGetDirectorResponsesList` и
 * `useCastingResponses`): ключ тот же, дата другая, поэтому и подпись другая.
 * Направление у пункта есть, в отличие от закладочного набора: бэкенд честно
 * переворачивает порядок по `sort_order`.
 */
export const ACTOR_RESPONSE_SORT_OPTIONS = [
  { value: 'created_at', label: 'По дате отклика' },
  { value: 'name', label: 'По алфавиту' },
  { value: 'age', label: 'По возрасту' }
] as const;

/**
 * «Естественные» направления сортировки актёров: даты — сначала свежие (desc),
 * «По алфавиту» и «По возрасту» — по возрастанию (asc). Совпадает с мобильным клиентом.
 *
 * Полный `Record`, а не `Partial`: направление нужно КАЖДОМУ ключу, и полнота карты
 * проверяется типом — забыть новый ключ сортировки TypeScript не даст. Из неё же
 * выводится домен ключей (`ACTOR_SORT_KEYS`).
 */
export const ACTOR_SORT_KEY_DEFAULTS: Record<TActorSortBy, TSortOrder> = {
  created_at: 'desc',
  name: 'asc',
  age: 'asc'
};

/**
 * Допустимые ключи сортировки актёрских списков — домен `sort_by` для валидации
 * значения из URL и из серверного пресета.
 *
 * Выводится из карты направлений, а НЕ из какого-то одного набора подписей: наборов
 * теперь три (поиск / закладки / отклики), они различаются только подписями, и вывод
 * домена из одного из них означал бы, что остальные экраны валидируют URL по чужому
 * списку — незаметно, пока значения совпадают, и молча узко или широко, как только
 * разъедутся. Домен у всех актёрских экранов ОДИН: ключ `sort_by` понимает и `users#search`,
 * и `bookmarks#users`, и `responses#index`.
 */
export const ACTOR_SORT_KEYS: TActorSortBy[] = Object.keys(
  ACTOR_SORT_KEY_DEFAULTS
) as TActorSortBy[];

/**
 * Сортировка актёрских списков по умолчанию. Её не пишет в URL `useListSort` и не
 * пишет в серверный пресет `buildSortPresetPayload` — пустое значение означает дефолт.
 */
export const ACTOR_DEFAULT_SORT: {
  sortBy: TActorSortBy;
  sortOrder: TSortOrder;
} = {
  sortBy: 'created_at',
  sortOrder: 'desc'
};

/**
 * Направление сортировки по умолчанию для поля актёрского списка — им добирается
 * направление, если в серверном пресете его нет или оно невалидно.
 *
 * Живёт рядом с картой направлений, а не копией на каждом экране: четыре экрана
 * поднимают сортировку из ОДНОГО пресета (`resolvePresetSort`), и разъехавшийся
 * fallback дал бы разный порядок на разных экранах от одного и того же payload.
 * @param value - поле сортировки
 */
export const getActorSortOrderDefault = (value: TActorSortBy): TSortOrder =>
  ACTOR_SORT_KEY_DEFAULTS[value];

/**
 * Опции сортировки списков кастингов, где `created_at` — дата самого кастинга (его
 * публикации): лента кастингов актёра и кастинги проекта режиссёра.
 *
 * Подпись называет критерий, а не «новизну»: тот же ключ на экране избранных кастингов
 * означает дату добавления в закладки (`CASTING_BOOKMARK_SORT_OPTIONS`), и общая
 * подпись врала бы на одном из двух смыслов.
 */
export const CASTING_SORT_OPTIONS = [
  { value: 'created_at', label: 'По дате публикации' },
  { value: 'application_deadline_date', label: 'По дедлайну заявок' },
  { value: 'filming_start', label: 'По дате съёмок' }
] as const;

/**
 * Опции сортировки списка ИЗБРАННЫХ кастингов.
 *
 * Отличается от `CASTING_SORT_OPTIONS` первым пунктом, и не только подписью:
 * `bookmarks#index` различает «`sort_by` не пришёл» (порядок добавления в закладки) и
 * «`sort_by=created_at`» (дата создания кастинга) — гейт стоит на
 * `sort_by_requested?`. Экран избранного шлёт «По дате добавления» первым способом —
 * то есть НЕ шлёт ключ сортировки вовсе, — а без ключа сервер игнорирует и
 * `sort_order`. Отсюда `nonDirectional`: стрелка направления у такого пункта была бы
 * обманом, перевернуть нечего.
 */
export const CASTING_BOOKMARK_SORT_OPTIONS = [
  { value: 'created_at', label: 'По дате добавления', nonDirectional: true },
  { value: 'application_deadline_date', label: 'По дедлайну заявок' },
  { value: 'filming_start', label: 'По дате съёмок' }
] as const;

/**
 * «Естественные» направления сортировки кастингов: дата публикации — сначала свежие
 * (desc), даты дедлайна и съёмок — от ближайшей (asc). Совпадает с мобильным клиентом.
 *
 * Полный `Record`, а не `Partial`: направление нужно КАЖДОМУ ключу, и полнота карты
 * проверяется типом. Из неё же выводится домен ключей (`CASTING_SORT_KEYS`).
 */
export const CASTING_SORT_KEY_DEFAULTS: Record<TCastingSortBy, TSortOrder> = {
  created_at: 'desc',
  application_deadline_date: 'asc',
  filming_start: 'asc'
};

/**
 * Допустимые ключи сортировки кастинговых списков — домен `sort_by` для валидации
 * значения из URL. Выводится из карты направлений, а не из набора подписей: наборы
 * (лента/проект и закладки) различаются подписью первого пункта, а домен ключей у них
 * общий — см. `ACTOR_SORT_KEYS`.
 */
export const CASTING_SORT_KEYS: TCastingSortBy[] = Object.keys(
  CASTING_SORT_KEY_DEFAULTS
) as TCastingSortBy[];

/**
 * Сортировка кастинговых списков по умолчанию — кастинговый двойник
 * `ACTOR_DEFAULT_SORT`. Её не пишет в URL `useListSort`: пустой параметр означает
 * дефолт. Каждому экрану кастингов нужен ровно этот ключ, и до появления константы
 * он стоял голым литералом `'created_at'` в четырёх местах — то есть смена дефолта
 * требовала найти их все.
 */
export const CASTING_DEFAULT_SORT: {
  sortBy: TCastingSortBy;
  sortOrder: TSortOrder;
} = {
  sortBy: 'created_at',
  sortOrder: 'desc'
};

/**
 * Опции сортировки списков проектов — таб «Проекты» на `/events` у актёра и страница
 * проектов режиссёра.
 *
 * Ни одной новой подписи: все три строки уже стоят в других наборах этого файла и
 * взяты оттуда побайтово — «По дате публикации» и «По дате съёмок» из
 * `CASTING_SORT_OPTIONS`, «По алфавиту» из актёрских наборов. Это не экономия, а
 * контракт: подписи сортировки совпадают с мобильным клиентом посимвольно, и
 * собственная формулировка для проектов означала бы четвёртую строку, которую
 * пришлось бы вводить и там (см. `modules/list-sorting.md`).
 *
 * `created_at`, а не `published_at`, и это тоже не мелочь: список проектов и до
 * сортировки шёл `.order(created_at: :desc)`, поэтому дефолтный порядок не меняется.
 * `published_at` у черновиков пуст — под `NULLS LAST` они уехали бы в конец, то есть
 * выдача изменилась бы молча, без просьбы пользователя.
 */
export const PROJECT_SORT_OPTIONS = [
  { value: 'created_at', label: 'По дате публикации' },
  { value: 'name', label: 'По алфавиту' },
  { value: 'filming_start', label: 'По дате съёмок' }
] as const;

/**
 * «Естественные» направления сортировки проектов: дата публикации — сначала свежие
 * (desc), «По алфавиту» и «По дате съёмок» — по возрастанию (asc). Совпадает с
 * кастинговым соседом (`CASTING_SORT_KEY_DEFAULTS`) в общих ключах.
 *
 * Полный `Record`, а не `Partial`: направление нужно КАЖДОМУ ключу, и полнота карты
 * проверяется типом. Из неё же выводится домен ключей (`PROJECT_SORT_KEYS`).
 */
export const PROJECT_SORT_KEY_DEFAULTS: Record<TProjectSortBy, TSortOrder> = {
  created_at: 'desc',
  name: 'asc',
  filming_start: 'asc'
};

/**
 * Допустимые ключи сортировки списков проектов — домен `sort_by` для валидации
 * значения из URL.
 *
 * Выводится из карты направлений, а НЕ из набора подписей: набор подписей у проектов
 * пока один, но именно так домен уже выведен у актёров и кастингов, и вывод из
 * `PROJECT_SORT_OPTIONS` заново связал бы домен с ОДНИМ набором подписей — ровно та
 * связь, которую в этой задаче из обоих других домена уже убрали.
 */
export const PROJECT_SORT_KEYS: TProjectSortBy[] = Object.keys(
  PROJECT_SORT_KEY_DEFAULTS
) as TProjectSortBy[];

/**
 * Сортировка списков проектов по умолчанию. Совпадает с порядком, который список
 * проектов имел до появления сортировки, и с дефолтом бэкенда
 * (`ProjectSorting`: `created_at DESC` при отсутствии `sort_by`), поэтому пустой URL
 * означает ровно прежнюю выдачу. `useListSort` эти значения в URL не пишет.
 */
export const PROJECT_DEFAULT_SORT: {
  sortBy: TProjectSortBy;
  sortOrder: TSortOrder;
} = {
  sortBy: 'created_at',
  sortOrder: 'desc'
};

/**
 * Направление сортировки по умолчанию для поля списка проектов — двойник
 * `getActorSortOrderDefault`. Живёт рядом с картой направлений, а не копией на
 * экране: списков проектов два (актёрский таб и страница режиссёра), и
 * разъехавшийся fallback дал бы им разный порядок от одного и того же выбора.
 * @param value - поле сортировки
 */
export const getProjectSortOrderDefault = (value: TProjectSortBy): TSortOrder =>
  PROJECT_SORT_KEY_DEFAULTS[value];

/**
 * Виды карточки визитки реестра кинокомпании (18698).
 *
 * Четвёртый вид — `table` — от ширины экрана не зависит и на узких экранах не
 * исчезает: таблица скроллится по горизонтали с закреплённой колонкой ФИО, и
 * прятать её значило бы, что выбранный на десктопе вид молча подменяется при
 * сужении окна. Мобильному клиенту этот вид не отдаётся вовсе — там видов три.
 */
export const ACTOR_CARD_VIEW_MODES: TActorCardViewMode[] = [
  'card',
  'list',
  'compact',
  'table'
];

/**
 * Опции сортировки реестра визиток кинокомпании (18698).
 *
 * Подписи — кросс-клиентский контракт с мобилкой, менять только синхронно: те же
 * четыре строки стоят в `uiKit.sortBottomSheet` мобильного клиента. Три из них уже
 * есть в других наборах этого файла и взяты оттуда побайтово («По дате добавления»
 * из закладочных наборов, «По алфавиту» и «По возрасту» — из актёрских), новая
 * ровно одна — «По статусу».
 *
 * `created_at` здесь означает дату добавления визитки в реестр, поэтому подпись
 * закладочная, а не «По дате регистрации»: регистрация профиля к визитке
 * отношения не имеет — визитка живёт и у незарегистрированного актёра.
 */
export const ACTOR_CARD_SORT_OPTIONS = [
  { value: 'created_at', label: 'По дате добавления' },
  { value: 'name', label: 'По алфавиту' },
  { value: 'age', label: 'По возрасту' },
  { value: 'status', label: 'По статусу' }
] as const;

/**
 * «Естественные» направления сортировки визиток: дата добавления — сначала свежие
 * (desc), остальные ключи — по возрастанию (asc: алфавит с «А», возраст с младших,
 * статус с `not_registered`). Совпадает с мобильным клиентом.
 *
 * Полный `Record`, а не `Partial`: направление нужно КАЖДОМУ ключу, и полнота карты
 * проверяется типом. Из неё же выводится домен ключей (`ACTOR_CARD_SORT_KEYS`).
 */
export const ACTOR_CARD_SORT_KEY_DEFAULTS: Record<
  TActorCardSortBy,
  TSortOrder
> = {
  created_at: 'desc',
  name: 'asc',
  age: 'asc',
  status: 'asc'
};

/**
 * Допустимые ключи сортировки реестра визиток — домен `sort_by` для валидации
 * значения из URL. Выводится из карты направлений, а не из набора подписей — как у
 * актёров, кастингов и проектов.
 */
export const ACTOR_CARD_SORT_KEYS: TActorCardSortBy[] = Object.keys(
  ACTOR_CARD_SORT_KEY_DEFAULTS
) as TActorCardSortBy[];

/**
 * Сортировка реестра визиток по умолчанию. Совпадает с порядком, который реестр
 * имел до появления контрола, и с дефолтом бэкенда (`ActorCardSorting`:
 * `created_at DESC` при отсутствии `sort_by`), поэтому пустой URL означает ровно
 * прежнюю выдачу. `useListSort` эти значения в URL не пишет.
 */
export const ACTOR_CARD_DEFAULT_SORT: {
  sortBy: TActorCardSortBy;
  sortOrder: TSortOrder;
} = {
  sortBy: 'created_at',
  sortOrder: 'desc'
};

/**
 * Направление сортировки по умолчанию для поля реестра визиток — двойник
 * `getProjectSortOrderDefault`. Живёт рядом с картой направлений: направление
 * добирается и общим контролом сортировки, и заголовком колонки в табличном виде,
 * и разъехавшийся fallback дал бы им разный порядок от одного и того же выбора.
 * @param value - поле сортировки
 */
export const getActorCardSortOrderDefault = (
  value: TActorCardSortBy
): TSortOrder => ACTOR_CARD_SORT_KEY_DEFAULTS[value];

/**
 * Форматы выгрузки в порядке показа. Подписи с расширением — кросс-клиентский
 * контракт с мобилкой (`producer.actorCards.export.*`): пользователь ищет файл
 * в «Загрузках» именно по расширению.
 */
export const EXPORT_OPTIONS = [
  { value: 'xlsx', label: 'В Excel (.xlsx)' },
  { value: 'pdf', label: 'В PDF (.pdf)' },
  { value: 'docx', label: 'В Word (.docx)' }
] as const satisfies ReadonlyArray<{ value: TExportFormat; label: string }>;

/**
 * Столбцы таблицы реестра визиток с подписями (18699).
 *
 * Подписи — кросс-клиентский контракт с мобилкой (`producer.actorCards.table.*` в
 * `ru.i18n.json`), менять только синхронно. Порядок — канонический: в нём набор
 * уходит в пресет, в нём же рисуются чекбоксы контрола и столбцы таблицы.
 *
 * «Фото» стоит первым, хотя отдельной колонки не имеет: чекбокс управляет аватаром
 * в ячейке ФИО, и в списке он читается как «первое, что видно в строке».
 */
export const ACTOR_CARD_COLUMN_OPTIONS = [
  { value: 'photo', label: 'Фото' },
  { value: 'name', label: 'ФИО' },
  { value: 'birth_date', label: 'Дата рождения' },
  { value: 'age', label: 'Возраст' },
  { value: 'gender', label: 'Пол' },
  { value: 'status', label: 'Статус' },
  { value: 'projects', label: 'Проекты' },
  { value: 'created_at', label: 'Добавлена' }
] as const satisfies ReadonlyArray<{
  value: TActorCardColumnKey;
  label: string;
}>;

/**
 * Домен ключей столбцов — выводится из набора подписей, а не наоборот: подписать
 * столбец обязательно (без подписи его нечем показать в контроле), поэтому набор
 * опций и есть единственный источник полноты.
 */
export const ACTOR_CARD_COLUMN_KEYS: TActorCardColumnKey[] =
  ACTOR_CARD_COLUMN_OPTIONS.map(option => option.value);

/**
 * Набор столбцов таблицы визиток по умолчанию — все, кроме «Даты рождения».
 *
 * Из двух столбцов об одном и том же в дефолте стоит ВОЗРАСТ (приёмка 18699):
 * кастинг спрашивает «сколько лет», и дата рождения отвечает на это только после
 * подсчёта в голове. Обе колонки остаются доступны чекбоксами — дата рождения
 * нужна там, где важен точный день (документы, договор).
 *
 * Дефолт в серверный пресет НЕ пишется (как и дефолтная сортировка) — отсутствие
 * ключа `table_columns` означает ровно этот набор, поэтому смена дефолта не
 * требует ни миграции persist, ни правок на бэкенде: у тех, кто уже сохранил свой
 * набор, ничего не меняется.
 */
export const ACTOR_CARD_DEFAULT_COLUMNS: TActorCardColumnKey[] = [
  'photo',
  'name',
  'age',
  'gender',
  'status',
  'projects',
  'created_at'
];

/**
 * Столбцы таблицы ИЗБРАННОГО режиссёра с подписями (18976) — двойник
 * `ACTOR_CARD_COLUMN_OPTIONS`. Порядок канонический: в нём набор уходит в
 * redux-persist и рисуются чекбоксы контрола «Столбцы».
 */
export const FAVORITE_ACTOR_COLUMN_OPTIONS = [
  { value: 'photo', label: 'Фото' },
  { value: 'name', label: 'ФИО' },
  { value: 'age', label: 'Возраст' },
  { value: 'gender', label: 'Пол' },
  { value: 'cities', label: 'Города' },
  { value: 'job_project_types', label: 'Опыт работы' },
  { value: 'folders', label: 'Папки' },
  { value: 'added_at', label: 'Добавлен в избранное' }
] as const satisfies ReadonlyArray<{
  value: TFavoriteActorColumnKey;
  label: string;
}>;

/** Домен ключей столбцов избранного — выводится из набора подписей */
export const FAVORITE_ACTOR_COLUMN_KEYS: TFavoriteActorColumnKey[] =
  FAVORITE_ACTOR_COLUMN_OPTIONS.map(option => option.value);

/**
 * Набор столбцов таблицы избранного по умолчанию — всё, кроме «Типов
 * проектов»: остаётся доступным чекбоксом, но реже всего нужен на первом экране.
 */
export const FAVORITE_ACTOR_DEFAULT_COLUMNS: TFavoriteActorColumnKey[] = [
  'photo',
  'name',
  'age',
  'gender',
  'cities',
  'folders',
  'added_at'
];

/**
 * Минимальная ширина таблицы избранного — то же число, что у реестра визиток:
 * обе таблицы одинаково не ужимаются, а скроллятся, с закреплённой колонкой ФИО.
 */
export const FAVORITE_ACTORS_TABLE_MIN_WIDTH = 900;
